/**
 * Colours and motion of the Notification Center, its toasts and the bell.
 * Every colour is a theme variable, so light, dark and custom themes all
 * apply; motion is calm and mechanical (no spring, no bounce).
 */
import type React from 'react'
import type { Transition } from 'framer-motion'

export const ACCENT = 'var(--theme-interactive-color)'

/** Status colours of the theme. */
export const STATUS = {
  success: 'var(--cdx-color-success)',
  danger: 'var(--cdx-color-danger)',
  warning: 'var(--cdx-color-warning)',
} as const

/**
 * A status or accent colour used for text or a glyph. Pulled toward the
 * theme's text colour so a light colour (the warning yellow, a pale custom
 * accent) stays readable on a light surface and a dark one on a dark surface.
 */
export function readable(color: string): string {
  return `color-mix(in srgb, ${color} 64%, var(--text-color))`
}

/** A soft tint of `color` behind a glyph in that colour. */
export function tint(color: string): React.CSSProperties {
  return { background: `color-mix(in srgb, ${color} 16%, transparent)`, color: readable(color) }
}

/** Filled accent, as used by the taskbar badge, with the theme's badge text colour. */
export const ACCENT_FILL: React.CSSProperties = {
  background: ACCENT,
  color: 'var(--taskbar-badge-text)',
}

export const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--theme-interactive-color)]'

/** The standard ease of the design system: quick, mechanical, no overshoot. */
export const EASE_STANDARD: [number, number, number, number] = [0.2, 0, 0.2, 1]

export const PANEL_TRANSITION: Transition = { type: 'tween', duration: 0.22, ease: EASE_STANDARD }

/** The floating panel (#1129): a short fade and 8px rise, like a desktop flyout. */
export const FLOAT_TRANSITION: Transition = { type: 'tween', duration: 0.18, ease: EASE_STANDARD }
export const FLOAT_OFFSET_PX = 8

/**
 * Elevation of the floating panel: the theme's own soft shadow token (navy-tinted
 * in light, deeper in dark), plus a whisper of blur so translucent window
 * backgrounds stay readable over busy wallpapers.
 */
export const FLOATING_SURFACE: React.CSSProperties = {
  boxShadow: 'var(--cdx-shadow-elev-1)',
  backdropFilter: 'blur(var(--cdx-glass-blur-window, 10px))',
  WebkitBackdropFilter: 'blur(var(--cdx-glass-blur-window, 10px))',
}

/**
 * Opaque surface for small layers over the rows (the action pill and its
 * menu, #1189). `--window-background` is translucent by design for the glass
 * windows, so on its own the row text below would show through. Painting it
 * over the theme's opaque base colour keeps the window tint and covers what
 * lies beneath in every theme.
 */
export const OPAQUE_SURFACE: React.CSSProperties = {
  background: 'linear-gradient(var(--window-background), var(--window-background)), var(--cdx-color-bg-base)',
}

/** Reduced motion: no slide, the element just appears or disappears. */
export const INSTANT_TRANSITION: Transition = { duration: 0 }
